import { useState, useEffect, useMemo, useContext } from 'react';

import { message } from 'antd';

import { ModalWrapper } from './style';

import { showProductPDFAndPCB } from '@lcsc-ui/apis/so';
import LcEdaCanvas from '@lcsc-ui/components/LcEdaCanvas';
import SvgIcon from '@lcsc-ui/components/SvgIcon';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { doPost, doGet } from '@lcsc-ui/utils/request';

export default function CadModel({ product, open, onCancel }) {
  const { resourceData } = useContext(LayoutContext);
  const [isOpen, setIsOpen] = useState(false);
  const [firstFileVo, setFirstFileVo] = useState({});
  const [fileType, setFileType] = useState('all');
  const [fileTypeVOList, setFileTypeVOList] = useState([]);
  const [svgData, setSvgData] = useState([]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [showMoreFile, setShowMoreFile] = useState(false);

  const fileList = useMemo(() => {
    if (fileType === 'all') {
      return fileTypeVOList.reduce((result, item) => {
        return result.concat(item.detailVOList);
      }, []);
    } else {
      const matchedType = fileTypeVOList.find((item) => item.fileType === fileType);
      return matchedType ? matchedType.detailVOList : [];
    }
  }, [fileTypeVOList, fileType]);

  const fileTypeList = useMemo(() => {
    const list = [
      {
        type: 'pdf_property',
        name: '规格书',
        icon: 'datasheet-pdf',
      },
      {
        type: 'certification_data_property',
        name: '认证资料',
        icon: 'datasheet-certification-data',
      },
      {
        type: 'industry_information',
        name: '行业资料',
        icon: 'datasheet-industry-information',
      },
      {
        type: 'reference_designs_property',
        name: '参考设计',
        icon: 'datasheet-reference-designs',
      },
    ];
    const fileTypeList = fileTypeVOList.map((item) => item.fileType);
    const filterList = list.filter((item) => fileTypeList.includes(item.type));
    return [
      {
        type: 'all',
        name: '全部',
      },
      ...filterList,
    ];
  }, [fileTypeVOList]);

  useEffect(() => {
    downloadFileInit();
  }, []);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const downloadFileInit = () => {
    showProductPDFAndPCB({
      annexNumber: product?.pdfDESProductId,
    }).then((res) => {
      if (res?.result?.status === 'success') {
        const { fileTypeVOList, productCode } = res.result;
        setFileTypeVOList(fileTypeVOList || []);
        // 当没有fileList时，看看fileTypeVOList有没有数据
        const firstFileVo =
          fileTypeVOList && fileTypeVOList[0] ? (fileTypeVOList[0].detailVOList || [])[0] || null : null;
        setFirstFileVo(firstFileVo);
        getSvgData(productCode);
      } else {
        message.error('查看数据手册失败');
      }
    });
  };

  // 加载EDA图
  const getSvgData = (productCode) => {
    doGet('https://lceda.cn', `/api/products/${productCode}/svgs`).then((res) => {
      if (res.success) {
        const data = res.result || [];
        if (!data.length) {
          setIsEmptyData(true);
        }
        setSvgData(data);
      } else {
        setIsEmptyData(true);
      }
    });
  };

  const getSearchByCodes = () => {
    const fd = new FormData();
    fd.append('codes[]', [product?.productCode]);
    doPost('https://pro.lceda.cn', '/api/devices/searchByCodes', fd).then((res) => {
      if (res.result) {
        const proResult = res?.result;
        if (!Array.isArray(proResult)) {
          return;
        }
        const attrs = proResult?.[0]?.attributes;
        const deviceUuid = proResult?.[0]?.uuid;
        const symbolUuid = attrs?.Symbol;
        const fooprintUuid = attrs?.Footprint;
        if (!fooprintUuid && !symbolUuid) {
          window.open('https://lceda.cn/');
          return;
        }
        // 拼接
        let proUrl = 'https://pro.lceda.cn/editor#tab=*';
        if (symbolUuid) {
          proUrl += '!' + symbolUuid;
          if (deviceUuid) {
            proUrl += `(device)${deviceUuid}@0819f05c4eef4c71ace90d822a990e87`;
          }
        }
        if (fooprintUuid) {
          if (symbolUuid) {
            proUrl += '|';
          }
          proUrl += '!' + fooprintUuid;
          if (deviceUuid) {
            proUrl += `(device)${deviceUuid}@0819f05c4eef4c71ace90d822a990e87`;
          }
        }
        window.open(proUrl);
      }
    });
  };

  const downloadFile = (file) => {
    const lcscAttaUrl = 'https://atta.szlcsc.com';
    window.open(file.linkAddress ? file.linkAddress : lcscAttaUrl + file.fileUrl + file.urlSign, '_blank');
  };

  const handleCancel = () => {
    onCancel();
  };

  let linkAddress = '';
  if (firstFileVo?.linkAddress) {
    linkAddress = firstFileVo?.linkAddress;
  } else {
    linkAddress = `${resourceData?.projectConfigVO?.lcscItemUrl}/datasheet/${encodeURIComponent(encodeURIComponent(product?.productModel))}/${product?.productId}.html`;
  }

  const DataSheetTemplate = () => {
    return (
      <>
        <div className="flex justify-between w-[99%]">
          {firstFileVo && (
            <div className="flex items-center h-[30px] leading-[30px]">
              <span className="inline-block w-[80px] text-[black]">数据手册：</span>
              <i className="w-[22px] -mt-[6px] h-[20px] inline-block file-icon"></i>
              <a
                className="text-[13px] flex-1 line-clamp-1"
                href={linkAddress}
                title={`点击在线预览${firstFileVo?.fileName}`}
                target="_blank"
                data-spm="a"
              >
                {firstFileVo?.fileName || firstFileVo?.linkAddress}
              </a>
            </div>
          )}
          <div className="flex items-center shrink-0">
            <button
              title={`点击下载文件${firstFileVo?.fileName}`}
              className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[80px] leading-[24px] h-[24px] hover:opacity-[0.6]"
              onClick={() => downloadFile(firstFileVo)}
            >
              {firstFileVo?.linkAddress ? '点击查看' : '点击下载'}
            </button>
            {(fileTypeVOList.length > 1 || fileTypeVOList[0]?.detailVOList?.length > 1) && (
              <button className="ml-[14px] text-lcBlue" onClick={() => setShowMoreFile(true)}>
                查看更多文件&gt;&gt;
              </button>
            )}
          </div>
        </div>
        <div className="w-[99%] h-[56px] bg-[#e3e9f1] rounded-[6px] flex mt-[10px]">
          <a ignore-spm href="https://lceda.cn" target="_blank">
            <SvgIcon iconName="cad_jlc" className="mt-[16px] ml-[14px] w-[143px] h-[24px]" />
          </a>
          <div className="ml-[25px] leading-[56px]">
            <a ignore-spm href="https://lceda.cn" target="_blank" className="text-[#000000]">
              嘉立创EDA是由嘉立创推出的国产EDA工具，具有100万库文件的免费PCB设计平台
            </a>
          </div>
        </div>
        <div className="mt-[20px] flex">
          <LcEdaCanvas type="sch" size={366} data={svgData} productModel={product?.productModel} />
          <LcEdaCanvas
            className="ml-[10px]"
            type="pcb"
            size={366}
            data={svgData}
            productModel={product?.productModel}
          />
        </div>
        <div className="flex justify-between mt-[25px] leading-[32px] text-[14px] text-[#000000] w-[99%]">
          <div>
            <span>若无法打开，请点击这里查看</span>
            <a
              ignore-spm
              className="text-[#0093E6] ml-[10px]"
              target="_blank"
              rel="nofollow"
              href="https://www.jlc-bbs.com/platform/a/527204"
            >
              <SvgIcon iconName="workaround" className="inline-block w-[18px] h-[16px]" />
              <span className="ml-[5px]">解决方法</span>
            </a>
            <a
              ignore-spm
              className="text-[#0093E6] ml-[16px] pl-[16px]  border-l-[1px] border-solid border-[#D9D9D9]"
              href={`https://lceda.cn/page/report-part-error?code=${product?.productCode}`}
              target="_blank"
              rel="nofollow"
            >
              <SvgIcon iconName="report" className="inline-block w-[18px] h-[16px]" />
              <span className="ml-[5px]">报告库错误</span>
            </a>
          </div>
          {isEmptyData ? (
            <a
              ignore-spm
              className="h-[32px] w-[100px] rounded-[4px] bg-[#1FA0E8] text-center leading-[32px] hover:bg-[#0093E6]"
              href={`https://lceda.cn/page/apply-new-part?code=${product?.productCode}`}
              target="_blank"
            >
              <span className="text-[#FFFFFF]">申请设计</span>
            </a>
          ) : (
            <button
              className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[100px] h-[32px] hover:opacity-[0.6]"
              onClick={getSearchByCodes}
            >
              立即打开
            </button>
          )}
        </div>
      </>
    );
  };

  const MoreFileTemplate = () => {
    return (
      <>
        <div className="flex justify-between">
          <div className="flex items-center">
            {fileTypeList.map((item) => (
              <button
                key={item.type}
                className={`mr-[10px] w-[110px] h-[40px] flex items-center justify-center rounded-[5px] border border-solid border-[#ededed] ${item.type === fileType ? 'bg-[#eefffd] border-[#99e9df]' : ''}`}
                onClick={() => {
                  setFileType(item.type);
                }}
              >
                {item.icon && <SvgIcon iconName={item.icon} className="mr-[10px] w-[20px] h-[23px]" />}
                {item.name}
              </button>
            ))}
          </div>
          <button className="ml-[14px] text-lcBlue" onClick={() => setShowMoreFile(false)}>
            返回上一页&gt;&gt;
          </button>
        </div>
        <div className="mt-[30px] h-[460px] overflow-auto">
          {fileList.map((item, index) => (
            <div className="mb-[20px] flex justify-between" key={index}>
              <div className="flex items-center">
                <SvgIcon iconName="datasheet-file" className="mr-[10px] w-[14px] h-[17px]" />
                <a
                  ignore-spm
                  href={item.linkAddress ? item.linkAddress : `https://atta.szlcsc.com${item.fileUrl}`}
                  target="_blank"
                >
                  {item.fileName}
                </a>
              </div>
              <button
                title={`点击下载文件${item.fileName}`}
                className="text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[80px] leading-[24px] h-[24px] hover:opacity-[0.6]"
                onClick={() => downloadFile(item)}
              >
                {item.linkAddress ? '点击查看' : '点击下载'}
              </button>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <ModalWrapper
      data-spm="xds"
      width="800px"
      title={`${product?.productCode} 数据手册`}
      cancelText=""
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      {showMoreFile ? <MoreFileTemplate /> : <DataSheetTemplate />}
    </ModalWrapper>
  );
}
